import { AxiosService } from '../../axios/axios.service';
import { tAxiosCustomConfig } from '../../axios/axios.types';
import {
  tSearchLeadMessagesQueryParams,
  tSearchLeadMessagesResponse,
  tSearchMessageIdentitiesResponse,
  tSearchMessagesQueryParams,
  tSearchMessagesResponse,
} from './MessageSearch.types';
import { tMessengerResponse } from './Messenger.types';

export const MessageSearchService = {
  // No base-URL flag: this resolves against BASE_URL_V2 (bff-web), the same
  // way MessengerService.searchLeads does.
  searchMessages: async function (
    currentWorkspaceId: string,
    queryParams: tSearchMessagesQueryParams,
    signal?: AbortSignal,
  ): Promise<tMessengerResponse<tSearchMessagesResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        params: queryParams,
        // Without this, a superseded search is only discarded client-side: the
        // request still completes, bff-web still forwards it and Postgres still
        // pays for a result nobody will read.
        signal,
      };

      const response = await AxiosService.get<
        tMessengerResponse<tSearchMessagesResponse>
      >(`/workspace/${currentWorkspaceId}/messages/search`, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  /**
   * Search the messages of ONE contact, across every chat it has (one per
   * channel), newest first — the in-chat search (SRE-3328).
   *
   * Same host and envelope as searchMessages. bff-web answers an empty 200 for
   * a query under 3 runes or made only of exclusions, but the caller must not
   * send those at all. 400 (a cursor chats-service cannot decode) and 404 are
   * relayed as they are.
   *
   * @param options.workspaceId - The workspace; membership is checked upstream.
   * @param options.leadId - The contact whose chats are searched.
   * @param options.queryParams - query, since, limit and the opaque cursor.
   * @param options.signal - Aborts a superseded search in flight.
   */
  searchLeadMessages: async function (options: {
    workspaceId: string;
    leadId: string;
    queryParams: tSearchLeadMessagesQueryParams;
    signal?: AbortSignal;
  }): Promise<tMessengerResponse<tSearchLeadMessagesResponse>> {
    const { workspaceId, leadId, queryParams, signal } = options;

    try {
      const config: tAxiosCustomConfig = { params: queryParams, signal };

      const response = await AxiosService.get<
        tMessengerResponse<tSearchLeadMessagesResponse>
      >(`/workspace/${workspaceId}/leads/${leadId}/messages/search`, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  /**
   * Resolve the display names behind one page of results.
   *
   * A second round trip on purpose. The names live in leads-service, and folding
   * the lookup into the search would put a second sequential upstream hop between
   * the query and the first painted row — the exact latency the 500 ms debounce
   * exists to protect. bff-web answers 200 with an empty list when leads-service
   * is down, so a degraded batch and an empty batch look the same here, which is
   * what the caller wants: both mean "keep the identity already on screen".
   */
  searchMessageIdentities: async function (
    currentWorkspaceId: string,
    handles: string[],
    signal?: AbortSignal,
  ): Promise<tMessengerResponse<tSearchMessageIdentitiesResponse>> {
    try {
      const config: tAxiosCustomConfig = {
        params: { handles: handles.join(',') },
        signal,
        // A failed enrichment is expected traffic, not an incident to page on.
        skipSentryCapture: true,
      };

      const response = await AxiosService.get<
        tMessengerResponse<tSearchMessageIdentitiesResponse>
      >(`/workspace/${currentWorkspaceId}/messages/search/identities`, config);

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  },
};
